// Phase 5 — Export / Import / Backup
import React, { useState } from "react";
import { motion } from "framer-motion";
import { Download, Upload, Database, AlertTriangle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Backup() {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [overwrite, setOverwrite] = useState(false);

  const isPDG = user?.role === "PDG";

  async function exportAll() {
    setDownloading(true);
    try {
      const { data } = await api.get("/export/all");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `grouperops_backup_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erreur d'export");
    } finally { setDownloading(false); }
  }

  async function importFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!confirm(`Importer ${file.name} ?${overwrite ? "\n\nATTENTION: les données existantes seront ÉCRASÉES." : ""}`)) return;
    setUploading(true);
    setResult(null);
    try {
      const text = await file.text();
      const json = JSON.parse(text);
      const { data } = await api.post("/import/json", { data: json.data || json, overwrite });
      setResult(data);
    } catch (err) {
      setResult({ ok: false, error: err?.response?.data?.detail || err.message });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  if (!isPDG) {
    return (
      <div className="card p-8 max-w-xl">
        <AlertTriangle size={20} className="text-warn mb-3" />
        <h3 className="font-heading text-lg font-bold mb-2">Accès réservé</h3>
        <p className="text-sm text-obsidian-400">Cette section est réservée au PDG.</p>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 max-w-3xl" data-testid="backup-page">
      <div>
        <div className="overline text-citron mb-2">Migration · Sauvegarde · Restauration</div>
        <h2 className="font-heading text-3xl font-bold tracking-tight">Sauvegardes</h2>
        <p className="text-sm text-obsidian-400 mt-1">Exportez l'intégralité des données du Groupe ou importez un dump JSON.</p>
      </div>

      <section className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Download size={16} className="text-citron" />
          <h3 className="font-heading text-lg font-bold tracking-tight">Export complet</h3>
        </div>
        <p className="text-sm text-obsidian-300 mb-5 leading-relaxed">
          Télécharge un fichier JSON contenant toutes les collections (sauf hashes mots de passe). Utile pour archivage, migration ou audit. Le fichier inclut : utilisateurs, trésorerie, comptabilité, RH, achats, stock, factures, véhicules, carburant, POS, chantiers, HSE, EPI, juridique, courrier, workflows, audit log.
        </p>
        <button onClick={exportAll} disabled={downloading} className="btn-primary" data-testid="export-button">
          <Download size={14} /> {downloading ? "Export…" : "Télécharger l'export JSON"}
        </button>
      </section>

      <section className="card p-6">
        <div className="flex items-center gap-2 mb-4">
          <Upload size={16} className="text-citron" />
          <h3 className="font-heading text-lg font-bold tracking-tight">Import / Restauration</h3>
        </div>
        <p className="text-sm text-obsidian-300 mb-5 leading-relaxed">
          Importez un fichier JSON (format export ROPS v2 ou structure compatible). Permet de migrer les données depuis votre ancienne app (Firebase ou autre). Les utilisateurs ne sont jamais importés (sécurité).
        </p>
        <label className="flex items-center gap-2 mb-4 cursor-pointer">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} className="accent-citron" />
          <span className="text-sm">
            <strong className="text-warn">Écraser</strong> les données existantes (sinon ajout)
          </span>
        </label>
        <label className="btn-secondary cursor-pointer inline-flex" data-testid="import-button">
          <Upload size={14} /> {uploading ? "Import…" : "Sélectionner un fichier JSON"}
          <input type="file" accept="application/json" onChange={importFile} className="hidden" />
        </label>

        {result && (
          <div className="mt-5">
            {result.ok ? (
              <div className="p-4 bg-ok/10 border border-ok/30 rounded-sm">
                <div className="flex items-center gap-2 text-ok mb-2"><CheckCircle2 size={14} /> Import terminé</div>
                <pre className="text-xs font-mono text-obsidian-200 overflow-x-auto">{JSON.stringify(result.counts, null, 2)}</pre>
              </div>
            ) : (
              <div className="p-4 bg-bad/10 border border-bad/30 rounded-sm">
                <div className="flex items-center gap-2 text-bad mb-2"><AlertTriangle size={14} /> Erreur</div>
                <div className="text-xs text-bad">{result.error}</div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="card p-6 border-citron/30">
        <div className="flex items-center gap-2 mb-3">
          <Database size={16} className="text-citron" />
          <h3 className="font-heading text-lg font-bold tracking-tight">Guide de déploiement production</h3>
        </div>
        <ol className="text-sm text-obsidian-300 space-y-2 list-decimal list-inside">
          <li>Configurez les secrets prod : <code className="font-mono text-citron">JWT_SECRET</code>, <code className="font-mono text-citron">MONGO_URL</code>, <code className="font-mono text-citron">EMERGENT_LLM_KEY</code></li>
          <li>Activez HTTPS + headers de sécurité (HSTS, CSP)</li>
          <li>Restreignez <code className="font-mono text-citron">CORS_ORIGINS</code> à votre domaine</li>
          <li>Activez l'authentification à 2 facteurs sur le compte PDG</li>
          <li>Mettez en place des sauvegardes automatiques quotidiennes (cron + export endpoint)</li>
          <li>Configurez un monitoring (Sentry / Datadog) et des alertes</li>
          <li>Migrez vos données existantes via l'import JSON ci-dessus</li>
        </ol>
      </section>
    </motion.div>
  );
}
